/**
 * The filter card. Layout follows
 * pages/Quests/components/quest-results/QuestResultFilters.tsx -- raw selects rather
 * than the shared Select, because that one takes a plain string[] and these need a
 * label that differs from the stored value.
 */
import { X } from 'lucide-react';
import Button from '../../../components/Button';
import { STATUS_LABELS } from '../types/accountDeletionTypes';
import { DEFAULT_FILTERS, type DeletionFilters } from '../utils/deletionFilters';

const CONTROL =
  'px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';

interface DeletionFiltersBarProps {
  filters: DeletionFilters;
  onChange: (filters: DeletionFilters) => void;
  /** How many records the current filters keep, for the summary line. */
  shown: number;
  total: number;
}

export const DeletionFiltersBar = ({
  filters,
  onChange,
  shown,
  total,
}: DeletionFiltersBarProps) => {
  const isDefault =
    filters.status === DEFAULT_FILTERS.status &&
    filters.source === DEFAULT_FILTERS.source &&
    filters.search === DEFAULT_FILTERS.search;

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="flex flex-col md:col-span-2">
          <label htmlFor="deletion-search" className="text-sm font-medium text-gray-700 mb-1">
            User ID
          </label>
          <div className="relative">
            <input
              id="deletion-search"
              type="text"
              value={filters.search}
              onChange={(event) => onChange({ ...filters, search: event.target.value })}
              placeholder="Enter or paste a uid"
              className={`${CONTROL} w-full pr-10`}
            />
            {filters.search && (
              <button
                type="button"
                onClick={() => onChange({ ...filters, search: '' })}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                aria-label="Clear the user ID filter"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col">
          <label htmlFor="deletion-status" className="text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            id="deletion-status"
            value={filters.status}
            onChange={(event) =>
              onChange({ ...filters, status: event.target.value as DeletionFilters['status'] })
            }
            className={CONTROL}
          >
            <option value="all">All statuses</option>
            <option value="attention">Needs attention</option>
            <option value="in_progress">{STATUS_LABELS.in_progress}</option>
            <option value="completed">{STATUS_LABELS.completed}</option>
            <option value="failed">{STATUS_LABELS.failed}</option>
          </select>
        </div>

        <div className="flex flex-col">
          <label htmlFor="deletion-source" className="text-sm font-medium text-gray-700 mb-1">
            Source
          </label>
          <select
            id="deletion-source"
            value={filters.source}
            onChange={(event) =>
              onChange({ ...filters, source: event.target.value as DeletionFilters['source'] })
            }
            className={CONTROL}
          >
            <option value="all">All sources</option>
            <option value="user">user — Auth trigger</option>
            <option value="backfill">backfill — tools script</option>
          </select>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
        <p className="text-xs text-gray-500">
          Showing {shown} of {total} loaded record{total === 1 ? '' : 's'}.
        </p>
        {!isDefault && (
          <Button variant="outline" size="sm" onClick={() => onChange(DEFAULT_FILTERS)}>
            Clear filters
          </Button>
        )}
      </div>
    </div>
  );
};
